import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ProductForm } from '../components/ProductForm';
import { ServiceForm } from '../components/ServiceForm';
import { LeadSaleForm } from '../components/LeadSaleForm';
import { BillingSettingsTab } from '../components/BillingSettingsTab';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Plus, Package, Wrench, FileText, Search, 
  Edit, Trash2, RefreshCw, X, Check,
  Calendar, Clock, Loader2, DollarSign, ShoppingBag
} from 'lucide-react';

function BillingPage() {
  var authContext = useAuth();
  var api = authContext.api;
  
  var stateHooks = {
    activeTab: useState('products'),
    products: useState([]),
    services: useState([]),
    leadSales: useState([]),
    loading: useState(true),
    showModal: useState(null),
    editItem: useState(null),
    searchTerm: useState('')
  };

  var activeTab = stateHooks.activeTab[0];
  var setActiveTab = stateHooks.activeTab[1];
  var products = stateHooks.products[0];
  var setProducts = stateHooks.products[1];
  var services = stateHooks.services[0];
  var setServices = stateHooks.services[1];
  var leadSales = stateHooks.leadSales[0];
  var setLeadSales = stateHooks.leadSales[1];
  var loading = stateHooks.loading[0];
  var setLoading = stateHooks.loading[1];
  var showModal = stateHooks.showModal[0];
  var setShowModal = stateHooks.showModal[1];
  var editItem = stateHooks.editItem[0];
  var setEditItem = stateHooks.editItem[1];
  var searchTerm = stateHooks.searchTerm[0];
  var setSearchTerm = stateHooks.searchTerm[1];

  useEffect(function() {
    loadAllData();
  }, []);

  function loadAllData() {
    setLoading(true);
    Promise.all([
      api.get('/api/billing/products'),
      api.get('/api/billing/services'),
      api.get('/api/billing/lead-sales')
    ]).then(function(results) {
      setProducts(results[0].data.products || []);
      setServices(results[1].data.services || []);
      setLeadSales(results[2].data.lead_sales || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function formatPrice(price) {
    if (price === null || price === undefined) return '-';
    return '$' + price.toFixed(2);
  }

  function getPricingBadges(item) {
    var badges = [];
    if (item.price_onetime) {
      badges.push({ type: 'onetime', label: '1x', price: item.price_onetime });
    }
    if (item.price_monthly) {
      badges.push({ type: 'monthly', label: 'Mo', price: item.price_monthly });
    }
    if (item.price_yearly) {
      badges.push({ type: 'yearly', label: 'Yr', price: item.price_yearly });
    }
    return badges;
  }

  // ============ PRODUCTS ============
  function renderProducts() {
    var filtered = products;
    if (searchTerm) {
      filtered = [];
      for (var i = 0; i < products.length; i++) {
        if (products[i].name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1) {
          filtered.push(products[i]);
        }
      }
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
            />
          </div>
          <Button onClick={function() { setEditItem(null); setShowModal('product'); }} className="bg-[#014DB7] hover:bg-[#003A8C]">
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>

        <div className="grid gap-4">
          {filtered.length > 0 ? renderProductCards(filtered) : (
            <div className="bg-white rounded-xl border p-12 text-center">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No products found</p>
              <Button onClick={function() { setEditItem(null); setShowModal('product'); }} className="mt-4 bg-[#014DB7]">
                <Plus className="w-4 h-4 mr-2" />
                Add First Product
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderProductCards(items) {
    var cards = [];
    for (var i = 0; i < items.length; i++) {
      (function(product) {
        var badges = getPricingBadges(product);
        cards.push(
          <div key={product.id} className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{product.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{product.description || 'No description'}</p>
                  {product.category && (
                    <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                      {product.category}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Pricing Badges */}
                <div className="flex gap-2">
                  {badges.length > 0 ? renderPriceBadges(badges) : (
                    <span className="text-sm text-gray-400">No pricing set</span>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-1">
                  <button onClick={function() { setEditItem(product); setShowModal('product'); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <Edit className="w-4 h-4 text-gray-500" />
                  </button>
                  <button onClick={function() { handleDeleteProduct(product.id); }} className="p-2 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })(items[i]);
    }
    return cards;
  }

  function renderPriceBadges(badges) {
    var elements = [];
    var colors = {
      onetime: 'bg-purple-100 text-purple-700 border-purple-200',
      monthly: 'bg-blue-100 text-blue-700 border-blue-200',
      yearly: 'bg-green-100 text-green-700 border-green-200'
    };
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      elements.push(
        <div key={badge.type} className={"px-3 py-1.5 rounded-lg border text-sm font-medium " + colors[badge.type]}>
          <span className="text-xs opacity-70">{badge.label}</span> ${badge.price}
        </div>
      );
    }
    return elements;
  }

  function handleDeleteProduct(id) {
    if (!window.confirm('Delete this product?')) return;
    api.delete('/api/billing/products/' + id).then(function() {
      toast.success('Product deleted');
      loadAllData();
    }).catch(function() { toast.error('Failed to delete'); });
  }

  // ============ SERVICES ============
  function renderServices() {
    var filtered = services;
    if (searchTerm) {
      filtered = [];
      for (var i = 0; i < services.length; i++) {
        if (services[i].name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1) {
          filtered.push(services[i]);
        }
      }
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
            />
          </div>
          <Button onClick={function() { setEditItem(null); setShowModal('service'); }} className="bg-[#014DB7] hover:bg-[#003A8C]">
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </div>

        <div className="grid gap-4">
          {filtered.length > 0 ? renderServiceCards(filtered) : (
            <div className="bg-white rounded-xl border p-12 text-center">
              <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No services found</p>
              <Button onClick={function() { setEditItem(null); setShowModal('service'); }} className="mt-4 bg-[#014DB7]">
                <Plus className="w-4 h-4 mr-2" />
                Add First Service
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderServiceCards(items) {
    var cards = [];
    for (var i = 0; i < items.length; i++) {
      (function(service) {
        var badges = getPricingBadges(service);
        cards.push(
          <div key={service.id} className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{service.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{service.description || 'No description'}</p>
                  {service.category && (
                    <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                      {service.category}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Pricing Badges */}
                <div className="flex gap-2">
                  {badges.length > 0 ? renderPriceBadges(badges) : (
                    <span className="text-sm text-gray-400">No pricing set</span>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-1">
                  <button onClick={function() { setEditItem(service); setShowModal('service'); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <Edit className="w-4 h-4 text-gray-500" />
                  </button>
                  <button onClick={function() { handleDeleteService(service.id); }} className="p-2 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })(items[i]);
    }
    return cards;
  }

  function handleDeleteService(id) {
    if (!window.confirm('Delete this service?')) return;
    api.delete('/api/billing/services/' + id).then(function() {
      toast.success('Service deleted');
      loadAllData();
    }).catch(function() { toast.error('Failed to delete'); });
  }

  // ============ MODALS ============
  function renderModal() {
    if (!showModal) return null;
    if (showModal === 'product') return renderProductModal();
    if (showModal === 'service') return renderServiceModal();
    if (showModal === 'leadsale') return renderLeadSaleModal();
    return null;
  }

  function renderProductModal() {
    return (
      <ModalWrapper title={editItem ? 'Edit Product' : 'Add Product'} onClose={function() { setShowModal(null); setEditItem(null); }}>
        <ProductForm 
          item={editItem} 
          onSave={function(data) {
            var promise = editItem 
              ? api.put('/api/billing/products/' + editItem.id, data)
              : api.post('/api/billing/products', data);
            promise.then(function() {
              toast.success(editItem ? 'Product updated' : 'Product created');
              setShowModal(null);
              setEditItem(null);
              loadAllData();
            }).catch(function() { toast.error('Failed to save'); });
          }}
          onClose={function() { setShowModal(null); setEditItem(null); }}
        />
      </ModalWrapper>
    );
  }

  function renderServiceModal() {
    return (
      <ModalWrapper title={editItem ? 'Edit Service' : 'Add Service'} onClose={function() { setShowModal(null); setEditItem(null); }}>
        <ServiceForm 
          item={editItem} 
          onSave={function(data) {
            var promise = editItem 
              ? api.put('/api/billing/services/' + editItem.id, data)
              : api.post('/api/billing/services', data);
            promise.then(function() {
              toast.success(editItem ? 'Service updated' : 'Service created');
              setShowModal(null);
              setEditItem(null);
              loadAllData();
            }).catch(function() { toast.error('Failed to save'); });
          }}
          onClose={function() { setShowModal(null); setEditItem(null); }}
        />
      </ModalWrapper>
    );
  }

  function renderLeadSaleModal() {
    return (
      <ModalWrapper title={editItem ? 'Edit Lead Sale' : 'Add Lead Sale'} onClose={function() { setShowModal(null); setEditItem(null); }}>
        <LeadSaleForm 
          item={editItem} 
          onSave={function(data) {
            var promise = editItem 
              ? api.put('/api/billing/lead-sales/' + editItem.id, data)
              : api.post('/api/billing/lead-sales', data);
            promise.then(function() {
              toast.success(editItem ? 'Lead sale updated' : 'Lead sale created');
              setShowModal(null);
              setEditItem(null);
              loadAllData();
            }).catch(function() { toast.error('Failed to save'); });
          }}
          onClose={function() { setShowModal(null); setEditItem(null); }}
        />
      </ModalWrapper>
    );
  }

  function renderLeadSales() {
    var filtered = leadSales;
    if (searchTerm) {
      filtered = leadSales.filter(function(item) { return item.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1; });
    }
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search lead sales..." value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} className="w-full pl-10 pr-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#84CC16]/30" />
          </div>
          <Button onClick={function() { setEditItem(null); setShowModal('leadsale'); }} className="bg-[#84CC16] hover:bg-[#65a30d] gap-2"><Plus className="w-4 h-4" /> Add Lead Sale</Button>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {filtered.map(function(item) {
            return (
              <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-green-600" /></div>
                    <div>
                      <h3 className="font-semibold text-[#0A1628]">{item.name}</h3>
                      <p className="text-xs text-gray-500">{item.description || item.category || 'Lead Sale'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {getPricingBadges(item).map(function(b) { return <span key={b.type} className={"px-2 py-1 rounded text-xs font-medium " + (b.type === 'onetime' ? 'bg-purple-100 text-purple-700' : b.type === 'monthly' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700')}>{b.label} {formatPrice(b.price)}</span>; })}
                    </div>
                    <button onClick={function() { setEditItem(item); setShowModal('leadsale'); }} className="p-2 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4 text-gray-500" /></button>
                    <button onClick={function() { handleDeleteLeadSale(item.id); }} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="text-center py-12 text-gray-400"><ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No lead sales yet</p></div>}
        </div>
      </div>
    );
  }

  function handleDeleteLeadSale(id) {
    if (!window.confirm('Delete this lead sale?')) return;
    api.delete('/api/billing/lead-sales/' + id).then(function() {
      toast.success('Lead sale deleted');
      loadAllData();
    }).catch(function() { toast.error('Failed to delete'); });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#014DB7]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Billing</h1>
          <p className="text-gray-500">Manage products and services with flexible pricing</p>
        </div>
        <Button onClick={loadAllData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Package className="w-6 h-6 text-blue-600" /></div>
            <div><p className="text-sm text-gray-500">Products</p><p className="text-2xl font-bold">{products.length}</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center"><Wrench className="w-6 h-6 text-purple-600" /></div>
            <div><p className="text-sm text-gray-500">Services</p><p className="text-2xl font-bold">{services.length}</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><ShoppingBag className="w-6 h-6 text-green-600" /></div>
            <div><p className="text-sm text-gray-500">Lead Sales</p><p className="text-2xl font-bold">{leadSales.length}</p></div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-1">
          <button
            onClick={function() { setActiveTab('products'); setSearchTerm(''); }}
            className={"flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px " +
              (activeTab === 'products' ? "border-[#014DB7] text-[#014DB7]" : "border-transparent text-gray-500 hover:text-gray-700")}
          >
            <Package className="w-4 h-4" />
            Products
          </button>
          <button
            onClick={function() { setActiveTab('services'); setSearchTerm(''); }}
            className={"flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px " +
              (activeTab === 'services' ? "border-[#014DB7] text-[#014DB7]" : "border-transparent text-gray-500 hover:text-gray-700")}
          >
            <Wrench className="w-4 h-4" />
            Services
          </button>
          <button
            onClick={function() { setActiveTab('leadsales'); setSearchTerm(''); }}
            className={"flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px " +
              (activeTab === 'leadsales' ? "border-[#84CC16] text-[#84CC16]" : "border-transparent text-gray-500 hover:text-gray-700")}
          >
            <ShoppingBag className="w-4 h-4" />
            Lead Sales
          </button>
          <button
            onClick={function() { setActiveTab('settings'); setSearchTerm(''); }}
            className={"flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px " +
              (activeTab === 'settings' ? "border-gray-700 text-gray-700" : "border-transparent text-gray-500 hover:text-gray-700")}
          >
            <DollarSign className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'products' && renderProducts()}
      {activeTab === 'services' && renderServices()}
      {activeTab === 'leadsales' && renderLeadSales()}
      {activeTab === 'settings' && <BillingSettingsTab api={api} />}

      {/* Modals */}
      {renderModal()}
    </div>
  );
}

// ============ COMPONENTS ============

function ModalWrapper(props) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={props.onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-[#0A1628]">{props.title}</h3>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6">
          {props.children}
        </div>
      </div>
    </div>
  );
}


export default BillingPage;
